import { useState, useEffect, useRef, useMemo, startTransition } from 'react';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import {
  fetchBusinessProfileById,
  selectBusinessProfileDetails,
  updateBusinessProfileEntry,
  BusinessProfileDetails,
  selectBusinessProfileStatus,
} from '../../slices/business-profile/businessProfileSlice';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectAllCompanies, updateCompany } from '../../slices/companies/companiesSlice';
import { isEmailEmpty, isValidEmail } from '../utils/email';

export function useDetailsCardForm(companyId: number) {
  const dispatch = useAppDispatch();
  const businessProfileDetails = useAppSelector(selectBusinessProfileDetails);
  const company = useAppSelector(selectAllCompanies)[0];
  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);
  const [isEdited, setIsEdited] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [showAddSocialModal, setShowAddSocialModal] = useState(false);
  const [companyNameValidated, setCompanyNameValidated] = useState(true);
  const [country, setCountry] = useState<{ value: string; title: string }>(
    null as unknown as { value: string; title: string }
  );
  const formRef = useRef<HTMLDivElement | null>(null);
  const [formData, setFormData] = useState<BusinessProfileDetails>({
    id: 0,
    company: companyId ? companyId : 1,
    business_email_address: 'randombusmail@gmail.com',
    business_phone_number: null,
    //marketing_opt_in: false,
    registration_number: null,
    registration_number_2_or_tax_number: null,
    business_country: null,
    registered_address: null,
    physical_address: null,
    postal_address: null,
  });

  const businessNameRegEx = useMemo(() => {
    return /^[a-zA-Z0-9 &\-]*$/;
  }, []);

  const cancelChanges = () => {
    setIsEdited(false);
    if (businessProfileDetails) {
      setFormData({
        ...businessProfileDetails,
      });
      if (businessProfileDetails.business_country) {
        setCountry({
          value: businessProfileDetails.business_country,
          title: businessProfileDetails.business_country,
        });
      }
    }
    if (company) {
      setCompanyName(company.name);
    }
  };

  useEffect(() => {
    if (company) {
      startTransition(() => {
        setCompanyName(company.name);
        setCountry({
          value: company.physical_country ?? '',
          title: company.physical_country ?? '',
        });
      });
    }
  }, [company]);
  useEffect(() => {
    if (businessProfileStatus === 'idle') {
      dispatch(fetchBusinessProfileById({ id: companyId }));
    }
  }, [dispatch, companyId, businessProfileStatus]);

  useEffect(() => {
    if (businessProfileDetails) {
      startTransition(() => {
        setFormData({
          ...businessProfileDetails,
        });
      });
      if (businessProfileDetails.business_country) {
        startTransition(() => {
          setCountry({
            value: businessProfileDetails.business_country ?? '',
            title: businessProfileDetails.business_country ?? '',
          });
        });
      }
    }
  }, [businessProfileDetails]);

  useEffect(() => {
    let showModalTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (isEdited && formRef.current && !formRef.current?.contains(event.target as Node)) {
        if (event.target instanceof HTMLElement) {
          const ignoredElements =
            event?.target?.closest('.MuiAutocomplete-popper') ||
            event?.target?.closest('.MuiTabs-root');
          if (!ignoredElements) {
            showModalTimeout = setTimeout(() => {
              setShowModal(true);
            }, 0);
          }
        }
      }
    };
    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (showModalTimeout) clearTimeout(showModalTimeout);
    };
  }, [isEdited]);

  const handleChange =
    <K extends keyof BusinessProfileDetails>(field: K) =>
    (value: BusinessProfileDetails[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCompanyNameChange = (value: string) => {
    setCompanyName(value);
    setCompanyNameValidated(() => businessNameRegEx.test(value));
  };

  const handleCheckboxChange = (field: string) => (value: boolean) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleValueChange = (field: string) => (value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const saveChanges = () => {
    if (!businessProfileDetails?.company) return;
    // Optional field, but it must hold a valid address when filled in.
    const businessEmail = formData.business_email_address;
    if (!isEmailEmpty(businessEmail) && !isValidEmail(businessEmail)) return;
    dispatch(
      updateBusinessProfileEntry({
        companyId: companyId,
        detailsId: Number(businessProfileDetails.id)!,
        data: { ...formData, business_country: country ? country.value : '' },
      })
    );
    dispatch(
      updateCompany({
        companyId,
        name: companyName.replaceAll('.', '').replaceAll(':', ''),
        physical_country: country.value,
        postal_country: country.value,
      })
    );

    setIsEdited(false);
    setShowModal(false);
  };

  const saveChangesSocialForm = () => {
    // dispatch(updateBusinessProfileSocials());
    toogleEditingSocialModal();

    //       dispatch(
    // // TODO: call slice to update soscials
    //       );
  };

  const toggleEditing = () => {
    setIsEdited((prev) => !prev);
  };

  const toogleEditingSocialModal = () => {
    setShowAddSocialModal((prev) => !prev);
  };

  const confirmExit = () => {
    setIsEdited(false);
    cancelChanges();
    setShowModal(false);
  };

  return {
    formData,
    handleChange,
    handleCompanyNameChange,
    handleCheckboxChange,
    handleValueChange,
    saveChanges,
    isEdited,
    setIsEdited,
    toggleEditing,
    formRef,
    showModal,
    setShowModal,
    confirmExit,
    showAddSocialModal,
    setShowAddSocialModal,
    toogleEditingSocialModal,
    saveChangesSocialForm,
    country,
    companyName,
    setCountry,
    companyNameValidated,
    cancelChanges,
  };
}
